"use client"

import {
  ChainsProvider,
  type ChainsProviderProps,
  CurrenciesProvider,
  type CurrenciesProviderProps,
} from "@opensea/app-kit"
import { GraphQLProvider } from "@opensea/graphql"
import type { Locale } from "@opensea/i18n"
import type { ThemeVariant } from "@opensea/ui-kit"
import {
  MediaContextProvider,
  SizeProvider,
  ToastProvider,
  ThemeProvider as UIKitThemeProvider,
} from "@opensea/ui-kit"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useState } from "react"
import { type Config, WagmiProvider } from "wagmi"
import { FlowHandler, type FlowHandlerProps } from "../flow"
import { MfaHandler } from "../mfa"
import { AccountEffects } from "./AccountEffects"
import type { BalancesProviderProps } from "./BalancesProvider"
import { BalancesProvider } from "./BalancesProvider"
import type { FlagProviderProps } from "./FlagProvider"
import { FlagProvider } from "./FlagProvider"
import { IntlProvider } from "./IntlProvider"
import {
  NavigationProvider,
  type NavigationProviderProps,
} from "./NavigationProvider"
import type { RenderImageProviderProps } from "./RenderImageProvider"
import { RenderImageProvider } from "./RenderImageProvider"
import { SiweProvider, type SiweProviderProps } from "./SiweProvider"
import { SolanaWalletProvider } from "./SolanaWalletProvider"
import type { TrackErrorProviderProps } from "./TrackErrorProvider"
import { TrackErrorProvider } from "./TrackErrorProvider"
import type { TrackEventProviderProps } from "./TrackEventProvider"
import { TrackEventProvider } from "./TrackEventProvider"

export type WalletProviderProps = {
  children: React.ReactNode
  theme?: ThemeVariant
  wagmiConfig: Config
  apiUrl?: string
  wsUrl?: string
  locale?: Locale
} & NavigationProviderProps &
  RenderImageProviderProps &
  ChainsProviderProps &
  SiweProviderProps &
  CurrenciesProviderProps &
  BalancesProviderProps &
  TrackErrorProviderProps &
  FlowHandlerProps &
  FlagProviderProps &
  TrackEventProviderProps

/**
 * Contains the full feature set required for styling, fetching, and authentication to work.
 * As of now this is only used in storybook.
 */
export const WalletProvider = ({
  children,
  theme,
  renderLink,
  renderImage,
  chains,
  siweAdapter,
  initialCurrencies,
  acceptTermsEnabled,
  trackError,
  locale,
  flags,
  flagsReady,
  trackEvent,
  wagmiConfig,
  apiUrl,
  wsUrl,
}: WalletProviderProps) => {
  const [queryClient] = useState(() => new QueryClient())

  return (
    <UIKitThemeProvider theme={theme}>
      <MediaContextProvider>
        <SizeProvider>
          <QueryClientProvider client={queryClient}>
            <GraphQLProvider
              apiUrl={apiUrl}
              errorHandler={undefined}
              wsUrl={wsUrl}
            >
              <IntlProvider locale={locale}>
                <ToastProvider>
                  <CoreWalletProvider
                    acceptTermsEnabled={acceptTermsEnabled}
                    chains={chains}
                    flags={flags}
                    flagsReady={flagsReady}
                    initialCurrencies={initialCurrencies}
                    renderImage={renderImage}
                    renderLink={renderLink}
                    siweAdapter={siweAdapter}
                    trackError={trackError}
                    trackEvent={trackEvent}
                    wagmiConfig={wagmiConfig}
                  >
                    {children}
                  </CoreWalletProvider>
                </ToastProvider>
              </IntlProvider>
            </GraphQLProvider>
          </QueryClientProvider>
        </SizeProvider>
      </MediaContextProvider>
    </UIKitThemeProvider>
  )
}

/**
 * This set of wallet providers contains the minimal amount needed to authenticate
 * and render the proper flows. For styling and layout to work properly, it is expected
 * for the client app to provide the relevant providers to ensure the flow works properly,
 * such as Theming, Media, and GraphQL.
 *
 * You must handle i18n in the client app through using the useMessages hook.
 */
export const CoreWalletProvider = ({
  children,
  siweAdapter,
  renderLink,
  renderImage,
  chains,
  initialCurrencies,
  initialBalances,
  acceptTermsEnabled = false,
  trackError,
  trackEvent,
  flags,
  flagsReady,
  wagmiConfig,
}: Omit<WalletProviderProps, "locale">) => {
  const [queryClient] = useState(() => new QueryClient())

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <FlagProvider
          flags={{ ...flags, acceptTermsEnabled }}
          flagsReady={flagsReady}
        >
          <TrackErrorProvider trackError={trackError}>
            <TrackEventProvider trackEvent={trackEvent}>
              <ChainsProvider chains={chains}>
                <CurrenciesProvider initialCurrencies={initialCurrencies}>
                  <BalancesProvider initialBalances={initialBalances}>
                    <NavigationProvider renderLink={renderLink}>
                      <RenderImageProvider renderImage={renderImage}>
                        <SolanaWalletProvider>
                          <SiweProvider siweAdapter={siweAdapter}>
                            {children}
                            <MfaHandler />
                            <FlowHandler
                              acceptTermsEnabled={acceptTermsEnabled}
                            />
                            <AccountEffects
                              acceptTermsEnabled={acceptTermsEnabled}
                              siweAdapter={siweAdapter}
                              trackError={trackError}
                            />
                          </SiweProvider>
                        </SolanaWalletProvider>
                      </RenderImageProvider>
                    </NavigationProvider>
                  </BalancesProvider>
                </CurrenciesProvider>
              </ChainsProvider>
            </TrackEventProvider>
          </TrackErrorProvider>
        </FlagProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}
